import React, { useState, useRef, useEffect } from "react";
import { SectionContainerDark } from "../common/sections/SectionContainerDark";
import { HeaderSection } from "../common/headers/HeaderSection";
import { extracurricularList } from "@/constants/index";
import { Extracurricular } from "@/interfaces/Extracurricular";

const section = [
  "Sekolah Dasar (SD)",
  "Sekolah Menengah Pertama (SMP)",
  "Sekolah Menengah Atas (SMA)",
];

const CodingSoftwareSection = () => {
  const [selected, setSelected] = useState("Sekolah Dasar (SD)");
  const [showModal, setShowModal] = useState(false);
  const [selectedItemData, setSelectedItemData] =
    useState<Extracurricular | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [screenWidth, setScreenWidth] = useState<number | null>(null);
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);

  const handleClick = () => {
    const phoneNumber = "6281215636018";
    const introduction = "Halo Mindu 1, perkenalkan saya";
    const name = "Nama:";
    const statement = "Pertanyaan: ";
    const message = `${introduction}\n${name}\n${statement}`;
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappURL, "_blank");
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageScrollRef = useRef<HTMLDivElement>(null);

  const scrollImages = (direction: "left" | "right") => {
    if (!imageScrollRef.current) return;
    const amount = 200;
    imageScrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const openModal = (item: Extracurricular) => {
    setSelectedItemData(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItemData(null);
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const filteredData: Extracurricular[] = extracurricularList.filter(
    (item) => item.section === selected
  );

  const scroll = (direction: "left" | "right") => {
    if (filteredData.length === 0) return;

    setCurrentIndex((prev) => {
      if (direction === "right") {
        return prev + 1 >= filteredData.length ? prev : prev + 1;
      } else {
        return prev - 1 < 0 ? prev : prev - 1;
      }
    });
  };

  useEffect(() => {
    setScreenWidth(window.innerWidth);

    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentIndex(0);
  }, [selected]);

  const getVisibleCards = () => {
    if (filteredData.length === 0) {
      return [];
    }

    const width = screenWidth || window.innerWidth;
    let maxCards;

    // Mobile: 1 card
    if (width < 640) {
      maxCards = 1;
    }
    // Tablet: 2 cards
    else if (width >= 640 && width < 1024) {
      maxCards = 2;
    }
    // Desktop: 3 cards
    else {
      maxCards = 3;
    }

    // Untuk mobile, ambil 1 card saja
    if (maxCards === 1) {
      const safeIndex = Math.min(currentIndex, filteredData.length - 1);
      const item = filteredData[safeIndex];
      return item ? [item] : [];
    }

    // Untuk tablet dan desktop
    const actualMaxCards = Math.min(maxCards, filteredData.length);
    const cardsToShow: Extracurricular[] = [];

    for (let i = 0; i < actualMaxCards; i++) {
      const idx = currentIndex + i;
      const item = filteredData[idx];
      if (item) {
        cardsToShow.push(item);
      }
    }

    return cardsToShow;
  };

  if (screenWidth === null) return null;

  const getVisibleCardCount = (width: number) => {
    if (width < 640) return 1;
    if (width < 1024) return 2;
    return 3;
  };

  const visibleCardCount = getVisibleCardCount(screenWidth);

  const showArrows = filteredData.length > visibleCardCount;
  const isFirstCard = currentIndex === 0;
  const isLastCard = currentIndex + visibleCardCount >= filteredData.length;
  // Untuk yang mempunyai multi level
  const isMultiLevel =
    selectedItemData?.section === "Sekolah Dasar (SD)" &&
    selectedItemData?.category === "Robotika, IoT & 3D Design";

  const currentData = isMultiLevel
    ? selectedItemData.levels?.[selectedLevelIndex]
    : selectedItemData;

  return (
    <SectionContainerDark
      id="coding-software-section"
      additionalClassName="bg-coding-software text-white py-6 px-4 md:px-12 font-body"
    >
      <HeaderSection
        title="Coding Software"
        subtitle="Cek berbagai materi populer hasil rancangan para ahli di sini. Materi baru tersedia setiap bulan tanpa biaya tambahan."
        darkMode
      />

      <div className="flex justify-center flex-wrap gap-4 mb-6">
        {section.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelected(cat)}
            className={`px-8 py-2 font-semibold text-base md:text-lg rounded-lg transition ${selected === cat
              ? "bg-teal-600 text-white"
              : "bg-white text-secondary-100 hover:bg-teal-600 hover:text-white"
              }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="relative mx-2 md:mx-32">
        {showArrows && !isFirstCard && (
          <button
            onClick={() => scroll("left")}
            className="absolute left-[-15px] md:left-[-40px] top-1/2 transform -translate-y-1/2 z-10"
          >
            <img src="/img/ekskul/left-arrow.png" alt="Kiri" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="flex gap-6 py-4 transition-all duration-300 ease-in-out justify-center md:justify-center"
        >
          {getVisibleCards().map((item, idx) => (
            <div
              key={`${item.category}-${idx}`}
              className="bg-white text-black rounded-lg shadow overflow-hidden flex flex-col w-[280px] md:w-[360px]"
            >
              <img
                src={item.image}
                alt={item.category}
                className="w-full h-[200px] md:h-[240px] object-cover"
              />
              <div className="p-4 flex flex-col justify-between flex-1">
                <div className="text-secondary-100 font-semibold">
                  <p>
                    <span>Kategori :</span> {item.category}
                  </p>
                  <p>
                    <span>Aplikasi :</span> {item.app}
                  </p>
                  <p>
                    <span>Bahasa Pemrograman :</span> {item.programmingLanguage}
                  </p>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 -mb-2">
                      {item.tags.map((tag, idx) => (
                        <span
                          key={`tag-${idx}`}
                          className="bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className={`flex items-center mt-8 ${item.category === "Customize"
                    ? "justify-center"
                    : "justify-between"
                    }`}
                >
                  {item.category !== "Customize" && (
                    <button
                      className="text-[#448ada] text-base font-bold hover:underline"
                      onClick={() => openModal(item)}
                    >
                      Lihat Detail
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (item.category === "Customize") {
                        handleClick();
                      } else {
                        window.open(
                          "https://wa.me/6281215636018",
                          "_blank"
                        );
                      }
                    }}
                    className="bg-teal-600 text-white font-semibold px-6 py-1 rounded hover:bg-teal-600 text-base"
                  >
                    Daftar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showArrows && !isLastCard && (
          <button
            onClick={() => scroll("right")}
            className="absolute right-[-15px] md:right-[-40px] top-1/2 transform -translate-y-1/2 z-10"
          >
            <img src="/img/ekskul/right-arrow.png" alt="Kanan" />
          </button>
        )}
      </div>

      {showModal && selectedItemData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
          onClick={handleClickOutside}
        >
          <div className="relative shadow-xl rounded-lg overflow-visible w-full sm:max-w-[1200px]">
            <div className="bg-white rounded-t-lg p-6 sm:p-8 pb-16">
              <div className="flex items-center justify-between -mb-16">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                  {selectedItemData.section} Coding Software
                </h2>
                <div className="flex items-center gap-4">
                  <a
                    href="https://wa.me/6281215636018"
                    target="_blank"
                    className="bg-teal-600 text-white px-4 sm:px-4 py-2 rounded text-base hover:bg-black duration-300 hover:text-white whitespace-nowrap"
                  >
                    Daftar Sekarang
                  </a>
                </div>
              </div>
            </div>

            {/* Body scrollable (mulai dari kategori) */}
            <div className="bg-white max-h-[75vh] overflow-y-auto rounded-b-lg p-6 sm:p-8 pt-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {isMultiLevel && selectedItemData.levels && (
                <div className="flex justify-center items-center flex-wrap gap-8 mb-4">
                  {selectedItemData.levels.map((level, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedLevelIndex(idx)}
                      className={`px-4 py-2 rounded text-sm font-semibold mt-4 ${selectedLevelIndex === idx
                        ? "bg-teal-600 text-white"
                        : "bg-gray-300 text-gray-700"
                        }`}
                    >
                      kelas {level.kelas}
                    </button>
                  ))}
                </div>
              )}

              {/* Detail mulai dari Kategori */}
              <div className="text-sm text-gray-700">
                <div className="grid grid-cols-4 py-2">
                  <div className="col-span-1 text-gray-500 font-medium">
                    Kategori
                  </div>
                  <div className="col-span-3">
                    {(currentData as Extracurricular).category}
                  </div>
                </div>

                <div className="grid grid-cols-4 py-2">
                  <div className="col-span-1 text-gray-500 font-medium">
                    Aplikasi
                  </div>
                  <div className="col-span-3">{currentData?.app}</div>
                </div>

                <div className="grid grid-cols-4 py-2">
                  <div className="col-span-1 text-gray-500 font-medium">
                    Bahasa Pemrograman
                  </div>
                  <div className="col-span-3">
                    {(currentData as Extracurricular).programmingLanguage}
                  </div>
                </div>

                <div className="grid grid-cols-4 py-2">
                  <div className="col-span-1 text-gray-500 font-medium">
                    Alat dan Device
                  </div>
                  <div className="col-span-3">Kesediaan sekolah atau murid</div>
                </div>

                <div className="grid grid-cols-4 py-2">
                  <div className="col-span-1 text-gray-500 font-medium">
                    Objektif
                  </div>
                  <div className="col-span-3">{currentData?.objective}</div>
                </div>

                <div className="grid grid-cols-4 py-2 pt-3">
                  <p className="col-span-1 text-gray-500 font-medium">
                    Detail Program
                  </p>
                  <div className="col-span-3">
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-x-6">
                      {/* Kolom 1 */}
                      <ol className="list-decimal pl-4 space-y-1 list-outside ">
                        {currentData?.detail?.slice(0, 8).map((item, index) => (
                          <li key={index} className="text-gray-700">
                            {item}
                          </li>
                        ))}
                      </ol>
                      {/* Kolom 2 */}
                      <ol start={9} className="list-decimal pl-4 space-y-1">
                        {currentData?.detail
                          ?.slice(8, 16)
                          .map((item, index) => (
                            <li key={index + 8} className="text-gray-700">
                              {item}
                            </li>
                          ))}
                      </ol>
                      {/* Kolom 3 */}
                      <ol start={17} className="list-decimal pl-4 space-y-1">
                        {currentData?.detail
                          ?.slice(16, 24)
                          .map((item, index) => (
                            <li key={index + 16} className="text-gray-700">
                              {item}
                            </li>
                          ))}
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gambar Scrollable */}
              {currentData?.images && (
                <div className="mt-8 w-full relative flex justify-center items-center">
                  <button
                    onClick={() => scrollImages("left")}
                    className="absolute -left-6 top-[35%] transform z-10 p-1 rounded-full lg:hidden"
                  >
                    <img
                      src="/img/ekskul/left-arrow.png"
                      alt="Kiri"
                      className="h-6 w-6"
                    />
                  </button>

                  <div
                    ref={imageScrollRef}
                    className="overflow-x-auto max-w-full scrollbar-none"
                  >
                    <div
                      className="grid grid-rows-2 auto-cols-max gap-4 w-max"
                      style={{ gridAutoFlow: "column" }}
                    >
                      {currentData.images.map(({ src, caption }, idx) => (
                        <div key={idx} className="w-44 text-center">
                          <img
                            src={src}
                            alt={caption}
                            className="h-32 w-full object-cover rounded border-2"
                          />
                          <p className="mt-1 text-sm text-black">{caption}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => scrollImages("right")}
                    className="absolute -right-7 top-[35%] transform z-10 p-1 rounded-full lg:hidden"
                  >
                    <img
                      src="/img/ekskul/right-arrow.png"
                      alt="Kanan"
                      className="h-6 w-6"
                    />
                  </button>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute -top-5 -right-5 w-12 h-12 flex items-center justify-center font-bold shadow-lg bg-gray-300 rounded-full text-gray-700 hover:bg-gray-400 hover:text-black text-3xl leading-none"
            >
              <span className="relative">&times;</span>
            </button>
          </div>
        </div>
      )}
    </SectionContainerDark>
  );
};

export default CodingSoftwareSection;
